import {
  Box,
  TextField,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  Button,
} from "@mui/material";
import { useEffect, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { add } from "date-fns";
import { useHistory, useLocation } from "react-router-dom";

import {
  api,
  AssetLocationTypes,
  IGetBookingsWithPairedJourneyParameters,
  RequestStatus,
} from "../../../../logic/api";
import { SessionContext } from "../../../../utility/useSession";
import { useBarcodeScanner } from "../../../../context/barcode-scanner-context";

interface ISearchProps {
  disabled: boolean;
  onSearch: (params: IGetBookingsWithPairedJourneyParameters) => void;
}

export default function Search({ disabled, onSearch }: ISearchProps) {
  const [bookingRef, setBookingRef] = useState("");
  const [vehicleRegistration, setVehicleRegistration] = useState("");
  const [currentLocationUuid, setCurrentLocationUuid] = useState("");
  const [maxDepartureDate, setMaxDepartureDate] = useState("");
  const [maxDepartureTime, setMaxDepartureTime] = useState("");
  const { t } = useTranslation();
  const { scan, resetScan } = useBarcodeScanner();
  const session = useContext(SessionContext);
  const history = useHistory();
  const { search } = useLocation();
  const arrivalsLocationsAPI = api.hooks.assetLocation.getByParent();
  const loadingArrivalsLocations =
    arrivalsLocationsAPI.status === RequestStatus.loading;
  const disableSearch =
    disabled || (!scan && !bookingRef && !vehicleRegistration);

  useEffect(() => {
    arrivalsLocationsAPI.send(session.current.context, {
      selectionTypes: [AssetLocationTypes.Lane],
      types: [AssetLocationTypes.Lane],
      parentId: 0,
      arrivalsOnly: true,
    });
  }, []);

  // Populate initial search from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    setBookingRef(searchParams.get("ref") || "");
    setVehicleRegistration(searchParams.get("reg") || "");
    setCurrentLocationUuid(searchParams.get("cl") || "");
    setMaxDepartureDate(searchParams.get("mdd") || "");
    setMaxDepartureTime(searchParams.get("mdt") || "");
  }, [search]);

  useEffect(() => {
    if (scan) {
      handleScan();
    }
  }, [scan]);

  const expectedReturnDate_lte = maxDepartureDate
    ? add(new Date(maxDepartureDate), {
      hours: Number(maxDepartureTime?.split(":")[0] || 23),
      minutes: Number(maxDepartureTime?.split(":")[1] || 59),
      seconds: 59,
    }).toISOString()
    : "";

  function handleScan() {
    if (disableSearch) {
      return;
    }

    setBookingRef("");
    setVehicleRegistration("");
    setCurrentLocationUuid("");
    onSearch({
      bookingRef: scan,
      expectedReturnDate_lte,
    });

    const params = new URLSearchParams();
    params.set("ref", scan);
    params.delete("reg");
    params.delete("cl");
    if (maxDepartureDate.trim()) {
      params.set("mdd", maxDepartureDate.trim());
    }
    if (maxDepartureTime.trim()) {
      params.set("mdt", maxDepartureTime.trim());
    }
    history.push({ search: params.toString() });
  }

  function handleSearch() {
    if (disableSearch) {
      return;
    }

    onSearch({
      bookingRef,
      vehicleRegistration,
      currentLocationUuid,
      expectedReturnDate_lte,
    });

    const params = new URLSearchParams();
    if (bookingRef.trim()) {
      params.set("ref", bookingRef.trim());
    }
    if (vehicleRegistration.trim()) {
      params.set("reg", vehicleRegistration.trim());
    }
    if (currentLocationUuid.trim()) {
      params.set("cl", currentLocationUuid.trim());
    }
    if (maxDepartureDate.trim()) {
      params.set("mdd", maxDepartureDate.trim());
    }
    if (maxDepartureTime.trim()) {
      params.set("mdt", maxDepartureTime.trim());
    }
    history.push({ search: params.toString() });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, my: 1 }}>
      <TextField
        fullWidth
        size="small"
        label={t("booking.labels.ref")}
        value={scan || bookingRef}
        onChange={(e) => {
          resetScan();
          setBookingRef(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSearch();
          }
        }}
        autoComplete="off"
        disabled={disabled}
      />
      <TextField
        fullWidth
        size="small"
        label={t("booking.labels.registration")}
        value={vehicleRegistration}
        onChange={(e) => {
          resetScan();
          setVehicleRegistration(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSearch();
          }
        }}
        autoComplete="off"
        disabled={disabled}
      />
      <FormControl
        size="small"
        disabled={disabled || loadingArrivalsLocations}
        fullWidth
      >
        <InputLabel id="current-location-filter-label">
          {loadingArrivalsLocations
            ? t("general.loading")
            : t("pair.currentLocationLabel")}
        </InputLabel>
        <Select
          labelId="current-location-filter-label"
          id="current-location-filter"
          value={currentLocationUuid}
          label={
            loadingArrivalsLocations
              ? t("general.loading")
              : t("pair.currentLocationLabel")
          }
          onChange={(e) => {
            setCurrentLocationUuid(e.target.value);
          }}
        >
          <MenuItem value="">
            <em>
              {loadingArrivalsLocations
                ? t("general.loading")
                : t("pair.currentLocationAllLabel")}
            </em>
          </MenuItem>
          {arrivalsLocationsAPI.data?.map((location) => (
            <MenuItem key={location.uuid} value={location.uuid}>
              {location.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        size="small"
        label={t("pair.maxDepartureDateLabel")}
        type="date"
        value={maxDepartureDate}
        onChange={(e) => {
          setMaxDepartureDate(e.target.value);
        }}
        inputProps={{
          min: new Date().toISOString().split("T")[0],
        }}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
      />
      <TextField
        fullWidth
        size="small"
        value={maxDepartureTime}
        onChange={(e) => {
          setMaxDepartureTime(e.target.value);
        }}
        label={t("pair.maxDepartureTimeLabel")}
        type="time"
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
      />
      <Button
        disabled={disableSearch}
        variant="contained"
        color="secondary"
        onClick={handleSearch}
      >
        {t("home.search.searchFieldLabel")}
      </Button>
    </Box>
  );
}
